import { HttpClient, HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { MsalModule } from '@azure/msal-angular';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import Aura from '@primeng/themes/aura';
import { providePrimeNG } from 'primeng/config';

import { ApiInterceptor } from './api/api.interceptor';
import { AppComponent } from './app.component';
import {
  MSALGuardConfigFactory,
  MSALInstanceFactory,
  MSALInterceptorConfigFactory
} from './auth-config';
import { AgricolaClientNotFoundDialogComponent } from './components/agricola/agricola-client-search/agricola-client-not-found-dialog/agricola-client-not-found-dialog.component';
import { AgricolaConsultasComponent } from './components/agricola/agricola-consultas/agricola-consultas.component';
import { AgricolaPolicyDetailComponent } from './components/agricola/agricola-policy-detail/agricola-policy-detail.component';
import { AgricolaUpdatePaymentDialogComponent } from './components/agricola/agricola-policy-detail/agricola-update-payment-dialog/agricola-update-payment-dialog.component';
import { AgricolaUpdateClientDialogComponent } from './components/agricola/agricola-policy-detail/agricola-update-client-dialog/agricola-update-client-dialog.component';
import { AgricolaCancelDialogComponent } from './components/agricola/agricola-policy-detail/agricola-cancel-dialog/agricola-cancel-dialog.component';
import { AgricolaClientSearchComponent } from './components/agricola/agricola-client-search/agricola-client-search.component';
import { AgricolaDashboardComponent } from './components/agricola/agricola-dashboard/agricola-dashboard.component';
import { AgricolaLoginComponent } from './components/agricola/agricola-login/agricola-login.component';
import { AgricolaNavbarComponent } from './components/agricola/agricola-navbar/agricola-navbar.component';
import { AgricolaPolicyWizardComponent } from './components/agricola/agricola-policy-wizard/agricola-policy-wizard/agricola-policy-wizard.component';
import { AgricolaReportQueueComponent } from './components/agricola/agricola-report-queue/agricola-report-queue.component';
import { AgricolaReportesComponent } from './components/agricola/agricola-reportes/agricola-reportes.component';
import { AgricolaReportConfirmDialogComponent } from './components/agricola/agricola-sell-report/agricola-report-confirm-dialog/agricola-report-confirm-dialog.component';
import { AgricolaSellReportComponent } from './components/agricola/agricola-sell-report/agricola-sell-report.component';
import { AgricolaBeneficiariesStepComponent } from './components/agricola/agricola-policy-wizard/steps/agricola-beneficiaries-step/agricola-beneficiaries-step.component';
import { AgricolaBeneficiaryDialogComponent } from './components/agricola/agricola-policy-wizard/steps/agricola-beneficiary-dialog/agricola-beneficiary-dialog.component';
import { AgricolaDependantsStepComponent } from './components/agricola/agricola-policy-wizard/steps/agricola-dependants-step/agricola-dependants-step.component';
import { AgricolaDependantDialogComponent } from './components/agricola/agricola-policy-wizard/steps/agricola-dependent-dialog/agricola-dependent-dialog.component';
import { AgricolaDeclaratoriaConfirmDialogComponent } from './components/agricola/agricola-policy-wizard/steps/agricola-declaratoria-confirm-dialog/agricola-declaratoria-confirm-dialog.component';
import { AgricolaDeclaratoriaStepComponent } from './components/agricola/agricola-policy-wizard/steps/agricola-declaratoria-step/agricola-declaratoria-step.component';
import { AgricolaExitConfirmDialogComponent } from './components/agricola/agricola-policy-wizard/steps/agricola-exit-confirm-dialog/agricola-exit-confirm-dialog.component';
import { AgricolaProductWarningDialogComponent } from './components/agricola/agricola-policy-wizard/steps/agricola-product-warning-dialog/agricola-product-warning-dialog.component';
import { AgricolaMainDataStepComponent } from './components/agricola/agricola-policy-wizard/steps/agricola-main-data-step/agricola-main-data-step.component';
import { AgricolaPaymentErrorDialogComponent } from './components/agricola/agricola-policy-wizard/steps/agricola-payment-step/agricola-payment-error-dialog/agricola-payment-error-dialog.component';
import { AgricolaPaymentStepComponent } from './components/agricola/agricola-policy-wizard/steps/agricola-payment-step/agricola-payment-step.component';
import { AgricolaPepScreenComponent } from './components/agricola/agricola-policy-wizard/steps/agricola-pep-screen/agricola-pep-screen.component';
import { AgricolaPremiumStepComponent } from './components/agricola/agricola-policy-wizard/steps/agricola-premium-step/agricola-premium-step.component';
import { AgricolaStepperComponent } from './components/agricola/agricola-stepper/agricola-stepper.component';
import { AgricolaToastComponent } from './components/agricola/agricola-toast/agricola-toast.component';
import { PublicPaymentFormComponent } from './components/public-payment/public-payment-form/public-payment-form.component';
import { SignatureCanvasComponent } from './components/public-payment/signature-canvas/signature-canvas.component';
import { SharedModule } from './modules/shared-module/shared-module.module';
import { RoutesModule } from './routes/routes.module';

export function httpLoaderFactory(http: HttpClient): TranslateHttpLoader {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

@NgModule({
  declarations: [
    AppComponent,
    AgricolaDashboardComponent,
    AgricolaClientSearchComponent,
    AgricolaLoginComponent,
    AgricolaNavbarComponent,
    AgricolaToastComponent,
    AgricolaStepperComponent,
    AgricolaPolicyWizardComponent,
    AgricolaMainDataStepComponent,
    AgricolaDeclaratoriaStepComponent,
    AgricolaPremiumStepComponent,
    AgricolaBeneficiariesStepComponent,
    AgricolaBeneficiaryDialogComponent,
    AgricolaDependantsStepComponent,
    AgricolaDependantDialogComponent,
    AgricolaDeclaratoriaConfirmDialogComponent,
    AgricolaExitConfirmDialogComponent,
    AgricolaProductWarningDialogComponent,
    AgricolaPaymentStepComponent,
    AgricolaPaymentErrorDialogComponent,
    AgricolaPepScreenComponent,
    AgricolaClientNotFoundDialogComponent,
    AgricolaSellReportComponent,
    AgricolaReportQueueComponent,
    AgricolaReportesComponent,
    AgricolaReportConfirmDialogComponent,
    PublicPaymentFormComponent,
    SignatureCanvasComponent,
    AgricolaConsultasComponent,
    AgricolaPolicyDetailComponent,
    AgricolaUpdatePaymentDialogComponent,
    AgricolaUpdateClientDialogComponent,
    AgricolaCancelDialogComponent
  ],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    HttpClientModule,
    SharedModule,
    RoutesModule,
    MsalModule.forRoot(
      MSALInstanceFactory(),
      MSALGuardConfigFactory(),
      MSALInterceptorConfigFactory()
    ),
    TranslateModule.forRoot({
      defaultLanguage: 'en',
      loader: {
        provide: TranslateLoader,
        useFactory: httpLoaderFactory,
        deps: [HttpClient]
      }
    })
  ],
  providers: [
    providePrimeNG({
      theme: {
        preset: Aura
      }
    }),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ApiInterceptor,
      multi: true
    }
  ],
  bootstrap: [AppComponent]
})
export class AppModule {}
